// Libraries
import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import axios, { isAxiosError } from 'axios';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
// Components
import { BearingLogo } from '@atoms/index';
import { SidebarSection } from '@molecules/index';
// Utils
import { LogoutIcon } from '@atoms/Icons/index';
import { getErrorMessage, triggerLogoutMessage } from '@helpers/index';
import text from '@translations/en.json';
import { config, LinersApiPaths, LOGOUT_DETAIL } from '@utils/index';
// Types
import { useSections } from '@hooks/index';
import { LayoutSidebarProps } from './LayoutSidebar.type';

/**
 * A responsive sidebar component that displays a navigation menu.
 * @param {LayoutSidebarProps} props - The props object containing the component's properties.
 * @param {boolean} props.isOpen - Indicates whether the sidebar is open or closed.
 * @param {Function} props.onClose - The function to call when the sidebar is closed.
 * @returns {ReactElement} The sidebar component.
 */
export const LayoutSidebar = ({ onClose, isOpen }: LayoutSidebarProps) => {
  const { data } = useSession();
  const { pathname } = useRouter();
  const { sections } = useSections();

  const handleLogout = async () => {
    try {
      const accessToken = data?.user?.accessToken;
      if (accessToken) {
        const {
          data: { detail },
        } = await axios.post<{ detail: string }>(
          `${config.services.apiGateway}${LinersApiPaths.Logout}`,
          {},
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (detail === LOGOUT_DETAIL) {
          triggerLogoutMessage('Logout triggered by user');
        }
      }
    } catch (error) {
      const detail = isAxiosError(error) ? error.response?.data?.detail : getErrorMessage(error);
      triggerLogoutMessage(detail);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <Drawer
      autoFocus={false}
      isOpen={isOpen}
      placement='left'
      onClose={onClose}
      returnFocusOnClose={false}
      onOverlayClick={onClose}
    >
      <DrawerOverlay />
      <DrawerContent maxW={'235px'}>
        <DrawerHeader p={0}>
          <Flex px={5} py={5} align='center' justify='space-between'>
            <BearingLogo w={24} h={8} />
            <IconButton
              p={2}
              h={8}
              minW={8}
              aria-label={text.close_menu}
              variant='baseLight'
              icon={<CloseIcon color='text.faded' />}
              onClick={onClose}
            />
          </Flex>
        </DrawerHeader>
        <DrawerBody as='nav' h='full' p={0} data-testid='sidebar-links-list'>
          <Flex direction='column' h='full' pb={4}>
            {sections.map((config) => {
              return <SidebarSection key={config.id} config={config} currentPath={pathname} onClose={onClose} />;
            })}
            <Button
              leftIcon={<LogoutIcon w={4} />}
              variant='ghost'
              mt='auto'
              mr='auto'
              fontSize='m'
              pl={5}
              w='full'
              fontWeight='medium'
              textAlign='left'
              justifyContent='start'
              onClick={handleLogout}
              data-testid='logout-button'
            >
              {text.log_out}
            </Button>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
